import type { CSSProperties } from "react"
import { Outlet } from "react-router-dom"
import { CalendarRange } from "lucide-react"

import { BOOK_KIND_OF, BOOK_META } from "@/components/BookMark"
import { PageHeader } from "@/components/PageHeader"
import { RequireEntity } from "@/components/layout/RequireEntity"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import { Label } from "@/components/ui/label"
import { useI18n, t } from "@/lib/i18n"
import { BOOK_LABELS, type BookId } from "@/lib/types"
import { cn } from "@/lib/utils"

import { BooksFiltersProvider, useBooksFilters } from "@/routes/books/context"
import { SeccionTabs } from "@/components/SeccionTabs"

const BOOK_IDS = Object.keys(BOOK_LABELS) as BookId[]

/**
 * Selector de libro como control segmentado y no como desplegable.
 *
 * Los tres libros estan siempre a la vista porque la idea central del producto
 * es que un mismo hecho vive en los tres. Un `select` los esconde y sugiere que
 * son opciones excluyentes de una lista larga; tres pestanas con su color dicen
 * "son tres vistas del mismo libro" y ademas se cambian de un clic.
 */
function BookSwitch() {
  const { book, setBook } = useBooksFilters()

  return (
    <div className="space-y-1">
      <Label id="libro-label">{t("Libro")}</Label>
      <div
        role="radiogroup"
        aria-labelledby="libro-label"
        className="inline-flex items-center gap-0.5 rounded-md border bg-surface p-0.5"
      >
        {BOOK_IDS.map((id) => {
          const kind = BOOK_KIND_OF[id]
          const meta = BOOK_META[kind]
          const Icon = meta.icon
          const active = book === id
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={active}
              title={meta.blurb}
              onClick={() => setBook(id)}
              className={cn(
                "relative flex h-6 items-center gap-1.5 rounded-[3px] px-2 text-xs font-medium",
                "transition-colors duration-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-surface-sunken text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
              style={active ? ({ "--spine-color": meta.token } as CSSProperties) : undefined}
            >
              <span
                aria-hidden="true"
                className="h-3 w-[2px] rounded-full transition-opacity"
                style={{ background: meta.token, opacity: active ? 1 : 0 }}
              />
              <Icon
                className="h-3 w-3"
                style={{ color: active ? meta.token : undefined }}
              />
              {BOOK_LABELS[id]}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function BooksToolbar() {
  const { from, setFrom, to, setTo } = useBooksFilters()

  return (
    <Toolbar>
      <BookSwitch />

      <div className="hidden self-stretch border-l md:block" aria-hidden="true" />

      <ToolbarField label={t("Desde")} htmlFor="desde">
        <SelectorDeFecha
          id="desde"
          value={from}
          onChange={setFrom}
          className="w-[8.5rem]"
        />
      </ToolbarField>

      <ToolbarField label={t("Hasta / corte")} htmlFor="hasta">
        <SelectorDeFecha
          id="hasta"
          value={to}
          onChange={setTo}
          className="w-[8.5rem]"
        />
      </ToolbarField>

      <ToolbarNote icon={CalendarRange} className="ml-auto">
        {t("Un mismo hecho vive en los tres libros. Cambiar de libro cambia lo que se ve, no el hecho.")}</ToolbarNote>
    </Toolbar>
  )
}

function BooksTabs() {
  const { t } = useI18n()
  const tabs = [
    { to: "diario", label: t("libro_diario") },
    { to: "mayor", label: t("libro_mayor") },
    { to: "balance", label: t("balance_comprobacion") },
    // La pantalla que explica el producto: por que el resultado legal y el
    // gerencial no son el mismo numero, y de que regla sale cada diferencia.
    { to: "comparar", label: "Comparar libros" },
  ]

  return (
    <SeccionTabs tabs={tabs} ariaLabel="Vistas del libro" />
  )
}

export function BooksLayout() {
  return (
    <BooksFiltersProvider>
      <div className="space-y-3">
        <PageHeader
          eyebrow="Vista contable"
          title={t("Libros")}
          description={t("El contador no trabaja en dashboards: trabaja en libros. Aqui estan el diario, el mayor —con cada asiento a un clic— y el balance de comprobacion, con el mismo rango de fechas y el mismo libro seleccionado.")}
        />

        <BooksTabs />
        <BooksToolbar />

        {/*
          Los cuatro libros cuelgan de una SOCIEDAD, no del cliente: el backend
          declara `entity_id` como Query(...) obligatoria en el diario, el mayor,
          el balance y el analisis (`_book_id()` busca el libro POR entidad). Sin
          sociedad elegida no hay peticion que mandar, asi que la pantalla lo
          pide con nombre y apellido en vez de traducir un 422 que nombra un
          campo que no esta en ningun formulario. Mismo criterio que en los tres
          maestros que cuelgan de la sociedad.
        */}
        <RequireEntity que="sus libros">
          <Outlet />
        </RequireEntity>
      </div>
    </BooksFiltersProvider>
  )
}
